import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ActivityIndicator, ScrollView, StyleSheet, TouchableOpacity, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {useManagementTheme, formatCurrency, formatNumber} from '../helpers/managementTheme';
import {
    MANAGEMENT_RANGE_PRESETS,
    buildChannelDistribution,
    buildManagementStatistics,
    buildMomentum,
    buildPaymentDistribution,
    buildRevenueSeries,
    fetchOrderStatisticsSummary,
    getManagementDateRange,
} from '../helpers/managementApi';
import {useManagementData} from '../helpers/ManagementDataContext';
import {EmptyState, InlineError, PrimaryButton, QuickActionCard, SectionTitle} from '../components/management/ManagementUI';
import {
    AreaSummaryCard,
    BarChart,
    ChartCard,
    ProgressRow,
    SegmentBar,
} from '../components/management/ManagementCharts';
import {ActionTile, AskAiCard, BubbleChart, GreetingHeader, HeroKpiCard, PillTabs} from '../components/premium';
import NotificationBellButton from '../components/NotificationBellButton';
import DemoModeToggleButton from '../components/DemoModeToggleButton';
import ChatHeaderButton from '../components/chat/ChatHeaderButton';
import {useCurrentUser} from '../helpers/useCurrentUser';
import {
    LowStockPanel,
    MeetingsPanel,
    OpenWorkOrdersPanel,
    RecentLeadsPanel,
    RecentOrdersPanel,
    RecentQuotesPanel,
    TimeTrackerPanel,
} from '../components/management/DashboardWidgets';
import DateRangePicker from '../components/management/DateRangePicker';
import Icon from '../components/management/SafeIcon';
import ManagementScreen from '../components/management/ManagementScreen';
import {useChatHubBadge} from '../helpers/teamChat/useChatHubBadge';
import PremiumStatCard from '../components/PremiumStatCard';
import {resolveDashboardProfile, useManagementAccess} from '../helpers/managementAccess';
import useAiAssistantGate from './assistant/components/useAiAssistantGate';
import {MANAGEMENT_HERO_TITLE_KEY} from '../helpers/managementDashboardProfile';
import {useManagementLayout} from '../helpers/managementLayout';
import {useDashboardPreferences} from '../helpers/useDashboardPreferences';
import {resolveActiveQuickActions, resolveActiveWidgets} from '../helpers/dashboardPreferences';

const QUICK_MODULES = [
    ['orders', 'mgmt_dash_quick_orders', 'clipboard-list-outline', 'info'],
    ['products', 'mgmt_dash_quick_products', 'food-fork-drink', 'success'],
    ['stock', 'mgmt_dash_quick_stock', 'warehouse', 'warning'],
    ['leads', 'mgmt_dash_quick_leads', 'account-tie-outline', 'violet'],
    ['customers', 'mgmt_dash_quick_customers', 'account-group-outline', 'info'],
    ['invoicesInbound', 'mgmt_dash_quick_invoices_inbound', 'file-import-outline', 'info'],
    ['invoicesOutbound', 'mgmt_dash_quick_invoices_outbound', 'file-export-outline', 'success'],
];

/**
 * Where tapping a KPI (hero or glass tile) opens — the same list the matching
 * quick module opens, so the figure is always one tap from its records.
 */
const KPI_TARGETS = {
    'kpi.revenue': 'orders',
    'kpi.orders': 'orders',
    'kpi.avgBasket': 'orders',
    'kpi.openOrders': 'orders',
    'kpi.products': 'products',
    'kpi.leads': 'leads',
    'kpi.customers': 'customers',
    'kpi.quotes': 'quotes',
    'kpi.lowStock': 'stock',
    'kpi.stockItems': 'stock',
    'kpi.workOrders': 'workOrders',
    'kpi.openWorkOrders': 'workOrders',
};

/** Frosted tiles the hero card carries before the remaining KPIs fall back to cards. */
const HERO_TILE_LIMIT = 4;

/**
 * KPI tile width per resolved column count. Percentages (not flex) because the
 * grid is a wrapping `flexWrap` row with a 12px gap — the shortfall from 100%
 * is what leaves room for that gap.
 */
const KPI_WIDTH_BY_COLUMNS = {1: '100%', 2: '48%', 3: '31.5%', 4: '23%'};

const isActiveOrder = (order) => !order?.storno && !['closed', 'cancelled'].includes(String(order?.status || '').toLowerCase());
const isOpenWorkOrder = (wo) => !['closed', 'completed', 'cancelled', 'invoiced'].includes(String(wo?.status || '').toLowerCase());
const isLowStock = (item) => Number(item?.stockQty ?? item?.stock ?? 0) <= 0;
const invoiceGross = (inv) => Number(inv?.grossAmount || inv?.totalGross || inv?.totals?.grossAmount || inv?.invoiceGrossAmount || 0);

/**
 * Prefer the server-side aggregated headline KPIs over the client-computed
 * ones when the `/order-statistics/summary` payload is available.
 *
 * Why: the client stats are built from the fetched order LIST, which is capped
 * at the backend's hard limit (5000 rows) — on a busy store a 30-day window
 * TRUNCATES, so gross/count/average silently under-report. The server summary
 * aggregates the full window in Mongo (no cap) and is therefore the more
 * correct figure. The list-derived stats stay as the offline/error fallback,
 * and every other field (channel splits, payment buckets, VAT map) keeps
 * coming from the list — this override touches ONLY the headline numbers the
 * summary actually covers.
 *
 * @param {object} clientStats Output of buildManagementStatistics.
 * @param {object|null} serverSummary Payload of fetchOrderStatisticsSummary.
 * @returns {object} clientStats, with gross/orderCount/averageBasket overridden
 *   (and `serverAggregated: true`) when the summary is usable.
 */
const mergeServerOrderSummary = (clientStats, serverSummary) => {
    const base = clientStats && typeof clientStats === 'object' ? clientStats : {};
    const totalOrders = Number(serverSummary?.ordersTotal);

    if (!serverSummary || typeof serverSummary !== 'object' || !Number.isFinite(totalOrders)) {
        return base;
    }

    return {
        ...base,
        gross: Number(serverSummary?.revenue?.gross) || 0,
        orderCount: totalOrders,
        averageBasket: Number(serverSummary?.averageOrderValue) || 0,
        serverAggregated: true,
    };
};

/**
 * Split an ordered widget list into render runs: consecutive KPI tiles collapse
 * into ONE grid so they share rows, while every panel stands alone.
 *
 * Doing this here (rather than rendering two fixed sections) is what lets a
 * user interleave — "revenue + orders, then the trend chart, then two more
 * tiles" — instead of being forced into the shipped "all KPIs, then all
 * panels" shape.
 *
 * @param {Array<{kind: string}>} widgets Ordered, already-gated widget definitions.
 * @returns {Array<{type: 'kpis'|'block', items: Array}>} Render runs in order.
 */
const buildRenderRuns = (widgets) => {
    const runs = [];

    (Array.isArray(widgets) ? widgets : []).forEach((widget) => {
        const previous = runs[runs.length - 1];

        if (widget.kind === 'kpi') {
            if (previous?.type === 'kpis') {
                previous.items.push(widget);
            } else {
                runs.push({type: 'kpis', items: [widget]});
            }

            return;
        }

        runs.push({type: 'block', items: [widget]});
    });

    return runs;
};

/**
 * Manager dashboard — a per-user, fully customizable board.
 *
 * What is on it, in what order and how dense the KPI grid is all come from
 * `user.dashboardConfig.mobileManagement` (see
 * {@link module:helpers/useDashboardPreferences}); this screen only knows how
 * to RENDER a widget id. A user who never customized anything gets their
 * store's profile defaults, which is the layout this screen used to hardcode.
 *
 * @param {object} [props] Optional embedding props (the tab navigator passes none).
 * @param {React.ReactNode} [props.workspaceBar] Workspace tab strip rendered under the greeting header (set when this
 *   screen is the "classic" workspace of the workspace dashboard).
 * @param {?() => void} [props.onOpenWorkspaces] Shows a header button that turns the workspace dashboard on.
 * @returns {JSX.Element} The dashboard.
 */
export default function ManagementDashboardScreen({workspaceBar = null, onOpenWorkspaces = null} = {}) {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const navigation = useNavigation();
    // Team Chat entry (chip on the "Ask anything" card + header button) — same gate + badge as every other chat entry point.
    const {visible: teamChatVisible, total: teamChatUnread} = useChatHubBadge();
    const {user: currentUser, firstName} = useCurrentUser();
    const {canAccess, context: accessContext} = useManagementAccess();
    // "Ask anything" AI assistant — build-flag + store-level gate (function.aiAssistant).
    const aiAssistantEnabled = useAiAssistantGate();
    const {isWide} = useManagementLayout();
    // Live dashboard data is owned by ManagementDataProvider, which background-
    // polls orders (and periodically the full aggregate) so the statistics tab
    // stays fresh without a per-screen fetch. See helpers/ManagementDataContext.js.
    const {data, scope, days, setDays, range, setRange, loading, refreshing, error, lastUpdatedAt, refresh} = useManagementData();

    // Role/placeType-aware emphasis. It no longer decides what renders — it
    // only seeds the DEFAULT layout for a user who never customized theirs, so
    // a service store still opens on work-order counters out of the box while
    // staying free to rearrange everything.
    const profile = useMemo(() => resolveDashboardProfile({
        placeType: scope?.restaurant?.placeType,
        roles: accessContext?.permissionsContext?.roles,
        canAccess,
    }), [scope, accessContext, canAccess]);

    const {preferences, loading: preferencesLoading} = useDashboardPreferences(profile);

    // Four KPI tiles per row on tablet at the default density; the user's own
    // choice still governs, doubled on a wide screen so a "1 per row" phone
    // layout does not become one absurdly stretched tile on an iPad.
    const kpiColumns = isWide
        ? Math.min((preferences.kpiColumns || 2) * 2, 4)
        : (preferences.kpiColumns || 2);
    const kpiWidth = KPI_WIDTH_BY_COLUMNS[kpiColumns] || KPI_WIDTH_BY_COLUMNS[2];

    // Time-of-day greeting for the hero eyebrow. The hero identity itself is
    // invariant: title stays t(MANAGEMENT_HERO_TITLE_KEY), subtitle stays the
    // store name — only this small eyebrow line is personalised.
    const greeting = useMemo(() => {
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 11) return t('mgmt_dash_greeting_morning', {defaultValue: 'Jó reggelt'});
        if (hour >= 11 && hour < 18) return t('mgmt_dash_greeting_day', {defaultValue: 'Szép napot'});

        return t('mgmt_dash_greeting_evening', {defaultValue: 'Jó estét'});
    }, [t]);

    // Personal eyebrow: "Jó reggelt, Ferenc" (falls back to the bare greeting while the user loads).
    const greetingLine = firstName
        ? t('mgmt_dash_greeting_named', {greeting, name: firstName, defaultValue: '{{greeting}}, {{name}}'})
        : greeting;

    // When a custom range is active, drive the day-count-dependent series off the
    // range's derived day count; otherwise use the preset day count.
    const effectiveDays = range?.days || days;

    // Server-side aggregated KPI summary (GET /order-statistics/summary) for
    // the same window — owned by this screen (the provider only carries the
    // order list) and re-fetched after every provider refresh so it follows
    // the same polling cadence. `null` until the first successful fetch; an
    // offline/error fetch resolves null and keeps the last good value.
    const [statsSummary, setStatsSummary] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const dateRange = range?.startDate && range?.endDate
            ? getManagementDateRange({startDate: range.startDate, endDate: range.endDate})
            : getManagementDateRange(days);
        fetchOrderStatisticsSummary(scope, dateRange).then((next) => {
            if (!cancelled && next) setStatsSummary(next);
        });
        return () => { cancelled = true; };
    }, [scope, days, range, lastUpdatedAt]);

    // Headline KPIs (gross / order count / average basket) prefer the
    // server-side window aggregate: the fetched list is capped at the backend
    // hard limit, so on a busy store a long window would silently under-report
    // if computed from the list alone. Channel/payment splits and the VAT map
    // keep coming from the list (the summary does not carry them all).
    const summary = useMemo(
        () => mergeServerOrderSummary(buildManagementStatistics(data.orders), statsSummary),
        [data.orders, statsSummary],
    );

    const revenueSeries = useMemo(() => buildRevenueSeries(data.orders, effectiveDays), [data.orders, effectiveDays]);
    const channelDist = useMemo(() => buildChannelDistribution(summary), [summary]);
    const paymentDist = useMemo(() => buildPaymentDistribution(summary), [summary]);
    const momentum = useMemo(() => buildMomentum(data.orders), [data.orders]);

    const lowStockCount = useMemo(() => data.stock.filter(isLowStock).length, [data.stock]);
    const activeOrders = useMemo(() => data.orders.filter(isActiveOrder).length, [data.orders]);
    const openWorkOrders = useMemo(() => data.workOrders.filter(isOpenWorkOrder).length, [data.workOrders]);
    const inboundSum = useMemo(() => data.invoicesInbound.reduce((a, b) => a + invoiceGross(b), 0), [data.invoicesInbound]);
    const outboundSum = useMemo(() => data.invoicesOutbound.reduce((a, b) => a + invoiceGross(b), 0), [data.invoicesOutbound]);
    const openRegisters = useMemo(() => data.registers.filter((r) => r?.isOpen || String(r?.status || '').toLowerCase() === 'open').length, [data.registers]);

    // CRM
    const crm = data.crmStats || {};
    const totalLeads = crm.totalLeads ?? crm.leadsCount ?? crm.total ?? data.leads.length;
    const wonLeads = crm.wonLeads ?? crm.converted ?? crm.conversions ?? 0;
    const pipelineValue = crm.pipelineValue ?? crm.totalValue ?? crm.value ?? 0;
    const conversionRate = totalLeads > 0 ? Math.round((wonLeads / totalLeads) * 100) : 0;

    const onRefresh = useCallback(() => {
        refresh();
    }, [refresh]);

    const openList = useCallback((type) => navigation.navigate('ManagementListScreen', {type}), [navigation]);
    const openCustomize = useCallback(() => navigation.navigate('ManagementDashboardCustomizeScreen'), [navigation]);

    const rangeLabel = range?.label
        || MANAGEMENT_RANGE_PRESETS.find((p) => p.value === days)?.label
        || t('mgmt_dash_range_days', {days});

    // Quick actions — the user's own pinned shortcuts. Each is gated through
    // the same canAccess/feature-flag rules as the module tiles, so a
    // capability this build or store hides never renders a dead shortcut even
    // if an older saved layout still lists it.
    const quickActions = useMemo(
        () => resolveActiveQuickActions(preferences, canAccess),
        [preferences, canAccess],
    );
    // Happy AI is a fixed, non-customizable quick action (not part of the
    // DASHBOARD_QUICK_ACTIONS catalog): it needs the store's AI kill switch on
    // top of the usual `canAccess` module/permission gate, which the generic
    // catalog has no concept of.
    const happyAiQuickActionVisible = aiAssistantEnabled && canAccess('happyAi').allowed;

    // The widgets to render, in the user's order. Same gating: a widget whose
    // module was revoked since the layout was saved simply drops out.
    const activeWidgets = useMemo(
        () => resolveActiveWidgets(preferences, canAccess),
        [preferences, canAccess],
    );
    const renderRuns = useMemo(() => buildRenderRuns(activeWidgets), [activeWidgets]);

    /**
     * Value/format/series descriptor per KPI widget id. Every figure here is
     * already computed above from the provider's data, so a KPI the user pins
     * costs nothing beyond its tile.
     */
    const kpiDescriptors = useMemo(() => ({
        'kpi.revenue': {label: t('mgmt_dash_kpi_revenue', {range: rangeLabel}), value: summary.gross || 0, format: formatCurrency, delta: momentum, series: revenueSeries},
        'kpi.orders': {label: t('mgmt_dash_kpi_orders'), value: summary.orderCount || 0, format: formatNumber, series: revenueSeries},
        'kpi.avgBasket': {label: t('mgmt_dash_kpi_avg_basket'), value: summary.averageBasket || 0, format: formatCurrency},
        'kpi.openOrders': {label: t('mgmt_dash_kpi_open_orders'), value: activeOrders, format: formatNumber},
        'kpi.products': {label: t('mgmt_dash_stat_product'), value: data.foods.length, format: formatNumber},
        'kpi.invoicesInbound': {label: t('mgmt_dash_stat_inbound'), value: inboundSum, format: formatCurrency},
        'kpi.invoicesOutbound': {label: t('mgmt_dash_stat_outbound'), value: outboundSum, format: formatCurrency},
        'kpi.openRegisters': {label: t('mgmt_dash_stat_open_register'), value: openRegisters, format: formatNumber},
        'kpi.openWorkOrders': {label: t('mgmt_dash_progress_open_work_orders'), value: openWorkOrders, format: formatNumber},
        'kpi.workOrders': {label: t('mgmt_dash_stat_work_order'), value: data.workOrders.length, format: formatNumber},
        'kpi.technicians': {label: t('mgmt_dash_stat_technician'), value: data.technicians.length, format: formatNumber},
        'kpi.appointments': {label: t('mgmt_dash_stat_appointment'), value: data.appointments.length, format: formatNumber},
        'kpi.leads': {label: t('mgmt_dash_stat_lead'), value: totalLeads, format: formatNumber},
        'kpi.conversion': {label: t('mgmt_dash_progress_conversion_rate'), value: conversionRate, format: (value) => `${formatNumber(value)}%`},
        'kpi.pipeline': {label: t('mgmt_dash_kpi_pipeline'), value: pipelineValue, format: formatCurrency},
        'kpi.customers': {label: t('mgmt_dash_stat_customer'), value: data.customers.length, format: formatNumber},
        'kpi.quotes': {label: t('mgmt_dash_stat_quote'), value: data.quotes.length, format: formatNumber},
        'kpi.lowStock': {label: t('mgmt_dash_stat_stock_shortage'), value: lowStockCount, format: formatNumber},
        'kpi.stockItems': {label: t('mgmt_dash_quick_stock'), value: data.stock.length, format: formatNumber},
        'kpi.warehouses': {label: t('mgmt_dash_stat_warehouse'), value: data.warehouses.length, format: formatNumber},
        'kpi.transactions': {label: t('mgmt_dash_stat_transaction'), value: data.transactions.length, format: formatNumber},
    }), [
        t, rangeLabel, summary, momentum, revenueSeries, activeOrders, data, inboundSum, outboundSum,
        openRegisters, openWorkOrders, totalLeads, conversionRate, pipelineValue, lowStockCount,
    ]);

    /**
     * Map one KPI widget onto the shared PremiumStatCard blueprint. A real
     * series renders as the faint background bar chart; cards without data fall
     * back to the decorative line, seeded so siblings don't repeat the shape.
     *
     * @param {object} widget Widget definition from the catalog.
     * @param {number} seed Fallback-shape seed (vary per sibling).
     * @returns {JSX.Element|null} The rendered stat card.
     */
    const renderKpiCard = (widget, seed) => {
        const kpi = kpiDescriptors[widget.id];

        if (!kpi) {
            return null;
        }

        return (
            <PremiumStatCard
                key={widget.id}
                icon={widget.icon}
                iconSet="community"
                tone={widget.tone}
                title={kpi.label}
                value={kpi.format ? kpi.format(kpi.value) : kpi.value}
                delta={kpi.delta != null ? kpi.delta : undefined}
                caption={kpi.delta != null ? t('mgmt_dash_kpi_vs_prev', {defaultValue: 'az előző időszakhoz képest'}) : undefined}
                chartData={kpi.series?.length ? kpi.series.map((d) => Number(d.value) || 0) : undefined}
                chartKind={kpi.series?.length ? 'bar' : 'line'}
                seed={seed}
                width={kpiWidth}
            />
        );
    };

    /**
     * Render a run of consecutive KPI widgets. The FIRST run of the board opens
     * with the orange hero card: its first widget is the headline figure (with
     * trend + sparkline when it has a real series) and the next few become frosted
     * tiles; anything beyond falls back to the regular stat-card grid. Later runs
     * stay plain stat-card grids so a deliberately interleaved layout is respected.
     *
     * @param {{items: Array}} run Consecutive KPI widgets.
     * @param {number} runIndex Position of the run in the board.
     * @returns {JSX.Element|null} The hero (+ grid) or the plain grid.
     */
    const renderKpiRun = (run, runIndex) => {
        if (runIndex !== 0) {
            return (
                <View key={`kpis-${runIndex}`} style={styles.statsGrid}>
                    {run.items.map((widget, index) => renderKpiCard(widget, runIndex + index))}
                </View>
            );
        }

        const entries = run.items
            .map((widget) => ({widget, kpi: kpiDescriptors[widget.id]}))
            .filter((entry) => !!entry.kpi);

        if (!entries.length) {
            return null;
        }

        const [lead, ...rest] = entries;
        const tiles = rest.slice(0, HERO_TILE_LIMIT);
        const cards = rest.slice(HERO_TILE_LIMIT);
        const target = (widget) => (KPI_TARGETS[widget.id] ? () => openList(KPI_TARGETS[widget.id]) : undefined);

        return (
            <View key={`kpis-${runIndex}`}>
                <HeroKpiCard
                    label={lead.kpi.label}
                    value={lead.kpi.format ? lead.kpi.format(lead.kpi.value) : lead.kpi.value}
                    delta={lead.kpi.delta != null ? lead.kpi.delta : undefined}
                    caption={lead.kpi.delta != null ? t('mgmt_dash_kpi_vs_prev', {defaultValue: 'az előző időszakhoz képest'}) : undefined}
                    series={lead.kpi.series}
                    onPress={target(lead.widget)}
                    tiles={tiles.map(({widget, kpi}) => ({
                        key: widget.id,
                        label: kpi.label,
                        icon: widget.icon,
                        value: kpi.format ? kpi.format(kpi.value) : kpi.value,
                        onPress: target(widget),
                    }))}
                />
                {cards.length > 0 && (
                    <View style={styles.statsGrid}>
                        {cards.map(({widget}, index) => renderKpiCard(widget, runIndex + index))}
                    </View>
                )}
            </View>
        );
    };

    /**
     * Render one full-width panel widget by id.
     *
     * @param {object} widget Widget definition from the catalog.
     * @returns {JSX.Element|null} The rendered block, or `null` for an id this build cannot draw.
     */
    const renderBlock = (widget) => {
        switch (widget.id) {
            case 'block.revenueTrend':
                return (
                    <View key={widget.id}>
                        <SectionTitle title={t('mgmt_dash_revenue_trend')} icon="chart-areaspline" eyebrow actionLabel={t('mgmt_dash_kpi_orders')} onPress={() => openList('orders')} />
                        <ChartCard
                            icon="finance"
                            tone="primary"
                            title={t('mgmt_dash_revenue_chart_title', {range: rangeLabel})}
                            subtitle={t('mgmt_dash_revenue_chart_subtitle')}
                        >
                            <View style={styles.chartTabs}>
                                <PillTabs
                                    options={MANAGEMENT_RANGE_PRESETS.map((preset) => ({value: preset.value, label: preset.label}))}
                                    value={range ? null : days}
                                    onChange={setDays}
                                />
                            </View>
                            <BarChart data={revenueSeries} valueFormat={formatCurrency} />
                        </ChartCard>
                    </View>
                );

            case 'block.channelSplit':
                return (
                    <View key={widget.id}>
                        <SectionTitle title={t('mgmt_dash_distributions')} icon="chart-donut" eyebrow />
                        <ChartCard icon="storefront-outline" tone="primary" title={t('mgmt_dash_sales_channels')} subtitle={t('mgmt_dash_sales_channels_subtitle')}>
                            <BubbleChart
                                data={channelDist.map(({label, value}) => ({label, value}))}
                                valueFormat={formatCurrency}
                                emptyText={t('mgmt_chart_no_distribution_data')}
                            />
                        </ChartCard>
                    </View>
                );

            case 'block.paymentSplit':
                return (
                    <ChartCard key={widget.id} icon="credit-card-outline" tone="info" title={t('mgmt_dash_payment_methods')} subtitle={t('mgmt_dash_payment_methods_subtitle')}>
                        <SegmentBar data={paymentDist} valueFormat={formatCurrency} />
                    </ChartCard>
                );

            case 'block.areaHospitality':
                return (
                    <AreaSummaryCard
                        key={widget.id}
                        icon="silverware-fork-knife"
                        tone="primary"
                        title={t('mgmt_dash_area_hospitality_title')}
                        subtitle={t('mgmt_dash_area_hospitality_subtitle')}
                        stats={[
                            {label: t('mgmt_dash_stat_revenue'), value: formatCurrency(summary.gross || 0)},
                            {label: t('mgmt_dash_stat_order'), value: formatNumber(summary.orderCount || 0)},
                            {label: t('mgmt_dash_stat_product'), value: formatNumber(data.foods.length)},
                        ]}
                        progress={{label: t('mgmt_dash_progress_closed_orders'), value: (summary.orderCount || 0) - activeOrders, max: summary.orderCount || 1}}
                        disabled={!canAccess('orders').allowed}
                        lockedReason={canAccess('orders').reason}
                        onPress={() => openList('orders')}
                    />
                );

            case 'block.areaService':
                return (
                    <AreaSummaryCard
                        key={widget.id}
                        icon="tools"
                        tone="warning"
                        title={t('mgmt_dash_area_service_title')}
                        subtitle={t('mgmt_dash_area_service_subtitle')}
                        stats={[
                            {label: t('mgmt_dash_stat_work_order'), value: formatNumber(data.workOrders.length)},
                            {label: t('mgmt_dash_stat_technician'), value: formatNumber(data.technicians.length)},
                            {label: t('mgmt_dash_stat_appointment'), value: formatNumber(data.appointments.length)},
                        ]}
                        progress={{label: t('mgmt_dash_progress_open_work_orders'), value: openWorkOrders, max: data.workOrders.length || 1, valueText: t('mgmt_dash_count_pcs', {count: openWorkOrders})}}
                        disabled={!canAccess('service').allowed}
                        lockedReason={canAccess('service').reason}
                        onPress={() => navigation.navigate('ManagementServiceScreen')}
                    />
                );

            case 'block.areaCrm':
                return (
                    <AreaSummaryCard
                        key={widget.id}
                        icon="account-tie-outline"
                        tone="violet"
                        title={t('mgmt_dash_area_crm_title')}
                        subtitle={pipelineValue > 0 ? t('mgmt_dash_area_crm_pipeline_value', {value: formatCurrency(pipelineValue)}) : t('mgmt_dash_area_crm_subtitle')}
                        stats={[
                            {label: t('mgmt_dash_stat_lead'), value: formatNumber(totalLeads)},
                            {label: t('mgmt_dash_stat_customer'), value: formatNumber(data.customers.length)},
                            {label: t('mgmt_dash_stat_quote'), value: formatNumber(data.quotes.length)},
                        ]}
                        progress={{label: t('mgmt_dash_progress_conversion_rate'), value: conversionRate, max: 100, valueText: `${conversionRate}%`}}
                        disabled={!canAccess('crm').allowed}
                        lockedReason={canAccess('crm').reason}
                        onPress={() => navigation.navigate('ManagementCrmScreen')}
                    />
                );

            case 'block.areaFinance':
                return (
                    <AreaSummaryCard
                        key={widget.id}
                        icon="cash-multiple"
                        tone="success"
                        title={t('mgmt_dash_area_finance_title')}
                        subtitle={t('mgmt_dash_area_finance_subtitle')}
                        stats={[
                            {label: t('mgmt_dash_stat_inbound'), value: formatNumber(data.invoicesInbound.length)},
                            {label: t('mgmt_dash_stat_outbound'), value: formatNumber(data.invoicesOutbound.length)},
                            {label: t('mgmt_dash_stat_open_register'), value: formatNumber(openRegisters)},
                        ]}
                        progress={{
                            label: t('mgmt_dash_progress_inbound_vs_outbound'),
                            value: outboundSum,
                            max: Math.max(1, inboundSum + outboundSum),
                            valueText: formatCurrency(outboundSum),
                        }}
                        disabled={!canAccess('finance').allowed}
                        lockedReason={canAccess('finance').reason}
                        onPress={() => navigation.navigate('ManagementFinanceScreen')}
                    />
                );

            case 'block.areaLogistics':
                return (
                    <AreaSummaryCard
                        key={widget.id}
                        icon="truck-fast-outline"
                        tone="info"
                        title={t('mgmt_dash_area_logistics_title')}
                        subtitle={t('mgmt_dash_area_logistics_subtitle')}
                        stats={[
                            {label: t('mgmt_dash_stat_warehouse'), value: formatNumber(data.warehouses.length)},
                            {label: t('mgmt_dash_stat_transaction'), value: formatNumber(data.transactions.length)},
                            {label: t('mgmt_dash_stat_stock_shortage'), value: formatNumber(lowStockCount)},
                        ]}
                        progress={{
                            label: t('mgmt_dash_progress_stock_shortage_items'),
                            value: lowStockCount,
                            max: data.stock.length || 1,
                            valueText: `${lowStockCount} / ${data.stock.length}`,
                        }}
                        disabled={!canAccess('logistics').allowed}
                        lockedReason={canAccess('logistics').reason}
                        onPress={() => navigation.navigate('ManagementLogisticsScreen')}
                    />
                );

            case 'block.operations':
                return (
                    <View key={widget.id}>
                        <SectionTitle title={t('mgmt_dash_operational_status')} icon="speedometer" eyebrow />
                        <ChartCard icon="gauge" tone="warning" title={t('mgmt_dash_daily_metrics')}>
                            <ProgressRow label={t('mgmt_dash_progress_active_orders')} value={activeOrders} max={summary.orderCount || 1} tone="info" valueText={t('mgmt_dash_count_pcs', {count: activeOrders})} />
                            <ProgressRow label={t('mgmt_dash_progress_open_service_work_orders')} value={openWorkOrders} max={data.workOrders.length || 1} tone="warning" valueText={t('mgmt_dash_count_pcs', {count: openWorkOrders})} />
                            <ProgressRow label={t('mgmt_dash_progress_crm_conversion')} value={conversionRate} max={100} tone="violet" valueText={`${conversionRate}%`} />
                            <ProgressRow label={t('mgmt_dash_stat_stock_shortage')} value={lowStockCount} max={data.stock.length || 1} tone="danger" valueText={t('mgmt_dash_count_pcs', {count: lowStockCount})} last />
                        </ChartCard>
                    </View>
                );

            case 'block.moduleShortcuts':
                return (
                    <View key={widget.id}>
                        <SectionTitle title={t('mgmt_dash_quick_functions')} icon="rocket-launch-outline" eyebrow />
                        <View style={styles.tileGrid}>
                            {QUICK_MODULES.map(([type, label, icon, tone]) => {
                                const access = canAccess(type);

                                return (
                                    <ActionTile
                                        key={type}
                                        icon={icon}
                                        tone={tone}
                                        title={t(label)}
                                        subtitle={t('mgmt_dash_quick_subtitle')}
                                        disabled={!access.allowed}
                                        lockedReason={access.reason}
                                        onPress={() => openList(type)}
                                    />
                                );
                            })}
                        </View>
                    </View>
                );

            case 'block.recentOrders':
                return <RecentOrdersPanel key={widget.id} orders={data.orders} onOpenList={() => openList('orders')} />;

            case 'block.openWorkOrders':
                return <OpenWorkOrdersPanel key={widget.id} workOrders={data.workOrders} isOpen={isOpenWorkOrder} onOpenList={() => openList('workOrders')} />;

            case 'block.recentQuotes':
                return <RecentQuotesPanel key={widget.id} quotes={data.quotes} onOpenList={() => openList('quotes')} />;

            case 'block.recentLeads':
                return <RecentLeadsPanel key={widget.id} leads={data.leads} onOpenList={() => openList('leads')} />;

            case 'block.lowStock':
                return <LowStockPanel key={widget.id} stock={data.stock} isLow={isLowStock} onOpenList={() => openList('stock')} />;

            case 'block.timeTracker':
                return (
                    <TimeTrackerPanel
                        key={widget.id}
                        scope={scope}
                        refreshToken={lastUpdatedAt}
                        onOpen={() => navigation.navigate('TimeTrackerScreen')}
                    />
                );

            case 'block.meetings':
                return (
                    <MeetingsPanel
                        key={widget.id}
                        refreshToken={lastUpdatedAt}
                        onOpen={() => navigation.navigate('MeetingRecorderScreen')}
                        onOpenMeeting={(id) => navigation.navigate('MeetingDetailScreen', {id})}
                    />
                );

            default:
                return null;
        }
    };

    if (loading || preferencesLoading) {
        return <View style={[styles.center, {backgroundColor: m.colors.bg}]}><ActivityIndicator size="large" color={m.colors.primary} /></View>;
    }

    // Store name, plus the active place when the manager is scoped to one.
    const scopeLabel = [scope?.restaurant?.name || t('mgmt_dash_hero_subtitle'), scope?.placeLabel]
        .filter((part, index, list) => !!part && list.indexOf(part) === index)
        .join(' · ');
    const showAskCard = aiAssistantEnabled || teamChatVisible;

    return (
        <ManagementScreen safeTop
            refreshing={refreshing}
            onRefresh={onRefresh}
        >
            <GreetingHeader
                user={currentUser}
                onAvatarPress={() => navigation.navigate('ManagementSettings')}
                eyebrow={greetingLine}
                title={t(MANAGEMENT_HERO_TITLE_KEY)}
                subtitle={scopeLabel}
                right={(
                    <View style={styles.headerActions}>
                        {/* Demo (Bemutató) toggle — self-gates to eligible roles; it lived on the old hero card. */}
                        <DemoModeToggleButton
                            color={m.colors.heading}
                            style={[styles.headerCircle, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}
                        />
                        <HeaderCircle onPress={openCustomize} icon="tune-variant" label={t('mgmt_dash_customize_edit')} />
                        {/* Workspaces entry - shown only until the user enables workspaces (then the tab strip below replaces it). */}
                        {!!onOpenWorkspaces && (
                            <HeaderCircle onPress={onOpenWorkspaces} icon="view-dashboard-edit-outline" label={t('wsd_enable')} />
                        )}
                        {teamChatVisible && (
                            <View style={[styles.headerCircle, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
                                <ChatHeaderButton size={21} color={m.colors.heading} />
                            </View>
                        )}
                        <View style={[styles.headerCircle, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
                            <NotificationBellButton size={21} color={m.colors.heading} />
                        </View>
                    </View>
                )}
            />

            {workspaceBar}

            <InlineError message={error} />

            <View style={styles.periodRow}>
                <View style={styles.periodPicker}>
                    <DateRangePicker
                        compact
                        days={range ? null : days}
                        startDate={range?.startDate}
                        endDate={range?.endDate}
                        label={range?.label}
                        onChange={setRange}
                    />
                </View>
                {refreshing && (
                    <View style={styles.reloadHint}>
                        <ActivityIndicator size="small" color={m.colors.primary} />
                    </View>
                )}
            </View>

            {renderRuns.length === 0 ? (
                <EmptyState
                    icon="view-dashboard-outline"
                    tone="primary"
                    title={t('mgmt_dash_empty_title')}
                    text={t('mgmt_dash_empty_text')}
                    action={(
                        <View style={styles.emptyAction}>
                            <PrimaryButton
                                label={t('mgmt_dash_customize_cta')}
                                icon="tune-variant"
                                onPress={openCustomize}
                            />
                        </View>
                    )}
                />
            ) : renderRuns.slice(0, 1).map((run, runIndex) => (run.type === 'kpis'
                ? renderKpiRun(run, runIndex)
                : renderBlock(run.items[0])))}

            {showAskCard && (
                <AskAiCard
                    title={t('assistant_hero_title', 'Kérdezz bármit az üzletedről')}
                    placeholder={t('assistant_input_placeholder', 'Kérdezz bármit…')}
                    onAsk={aiAssistantEnabled ? () => navigation.navigate('AIAssistantScreen') : undefined}
                    chatLabel={teamChatVisible ? t('team_chat_hub_title') : undefined}
                    chatBadge={teamChatUnread}
                    onChat={() => navigation.navigate('ChatHomeScreen')}
                />
            )}

            {(quickActions.length > 0 || happyAiQuickActionVisible) && (
                <>
                    <SectionTitle
                        title={t('mgmt_dash_quick_actions', {defaultValue: 'Gyorsműveletek'})}
                        icon="flash"
                        eyebrow
                        actionLabel={t('mgmt_dash_customize_edit')}
                        onPress={openCustomize}
                    />
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.quickActionsScroller}>
                        {happyAiQuickActionVisible && (
                            <QuickActionCard
                                icon="creation"
                                tone="primary"
                                label={t('ai.title', {defaultValue: 'Happy AI'})}
                                onPress={() => navigation.navigate('HappyAiInsightsScreen')}
                            />
                        )}
                        {quickActions.map((action) => (
                            <QuickActionCard
                                key={action.id}
                                icon={action.icon}
                                tone={action.tone}
                                label={t(action.labelKey)}
                                onPress={() => navigation.navigate(action.screen, action.params)}
                            />
                        ))}
                    </ScrollView>
                </>
            )}

            {renderRuns.slice(1).map((run, index) => (run.type === 'kpis'
                ? renderKpiRun(run, index + 1)
                : renderBlock(run.items[0])))}
        </ManagementScreen>
    );
}

/**
 * Round 44 px header action (customize…). Sits beside the chat / bell circles.
 *
 * @param {{icon: string, label: string, onPress: Function}} props
 * @returns {JSX.Element} The circle button.
 */
function HeaderCircle({icon, label, onPress}) {
    const m = useManagementTheme();

    return (
        <TouchableOpacity
            onPress={onPress}
            activeOpacity={0.8}
            accessibilityRole="button"
            accessibilityLabel={label}
            style={[styles.headerCircle, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}
        >
            <Icon name={icon} size={20} color={m.colors.heading} />
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    center: {flex: 1, justifyContent: 'center', alignItems: 'center'},
    statsGrid: {flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 16},
    periodRow: {marginBottom: 16, flexDirection: 'row', alignItems: 'center', gap: 12},
    headerActions: {flexDirection: 'row', alignItems: 'center', gap: 8},
    headerCircle: {width: 42, height: 42, borderRadius: 21, borderWidth: 1, alignItems: 'center', justifyContent: 'center'},
    chartTabs: {marginBottom: 12, marginLeft: -6},
    tileGrid: {flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between'},
    periodPicker: {flex: 1},
    reloadHint: {paddingHorizontal: 4},
    quickActionsScroller: {marginBottom: 16, flexGrow: 0},
    emptyAction: {flexDirection: 'row'},
});
